/**
 * REF-2 / REF-5 — the Refund Log, with sort and filter.
 *
 * One row per refund event, carrying the Refund Type (Full / Item / Fee) and the component
 * amounts that make up the Refunded total — a reader who cannot see the components cannot
 * tell a $26 tax refund from a $26 lost sale.
 *
 * The filters narrow the LOG only. The component split, reason and staff cards above it
 * describe the whole period and are never rewritten by a filter here: quoting a fee-only
 * total as "the period's refunds" is exactly the misreading the REF epic exists to prevent.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { T, Bone, useIsDesktop } from '../reportTokens';
import SectionEmpty from '../SectionEmpty';
import SectionError from '../SectionError';
import TruncatedCell from '../TruncatedCell';
import { fmtCurrency } from '../../drillHelpers';
import { useAuth } from '../../../context/AuthContext';
import { useBranch } from '../../../context/BranchContext';
import { useServiceChargeEnabled } from '../../../hooks/useServiceChargeEnabled';
import { ReportSectionExportButton } from '../components/ReportExportButton';
import type { ExportFormat, ExportCell } from '../../../utils/reportExport';
import { getRefundLogApi } from '../../../redux/reports/clickHouse/clickHouseApi';
import { RefundCard, RefundTypeBadge } from './RefundSections';
import {
    REFUND_TYPE_LABEL,
    itemRefundAmount,
    refundTotal,
    taxRefundAmount,
    tipsAndGratuityRefundAmount,
    type RefundRecord,
    type RefundSalesSource,
    type RefundType,
} from './refundModel';
import { refundLogItemsToRecords } from './refundLogRow';
import { refundLogExportHeaders, refundLogExportRow, refundLogExportTotalsRow } from './refundLogExport';
import { useRefundLogTableQuery, type RefundLogTotals } from './useRefundLogTableQuery';
import { useRefundLogFilterOptions } from './useRefundLogFilterOptions';
import type { RefundLogFilterKind } from '../../../interface/clickHouseInterface';
import {
    COMPONENT_FILTER_LABEL,
    DEFAULT_REFUND_LOG_SORT,
    EMPTY_REFUND_LOG_FILTERS,
    FIRST_CLICK_DIR,
    MOBILE_SORTS,
    REFUND_LOG_SORT_FIELD,
    filterRefundLog,
    type RefundComponentFilter,
    type RefundLogFilterChange,
    type RefundLogFilters,
    type RefundLogSort,
    type RefundLogSortKey,
} from './refundLogQuery';

/** API 'Full'/'Item'/'Fee' → the FE's own RefundType key, for the Type dropdown. */
const LABEL_TO_TYPE: Record<string, RefundType> = { Full: 'full', Item: 'item', Fee: 'fee' };

/** A visibly darker separator dot for the mobile card's otherwise-faint meta line —
 * the line's own T.t3 made a plain '·' character nearly disappear once it carried five
 * segments (date, channel, items, tax, tips & grat) instead of two. */
const Dot = () => <span style={{ color: T.t2, fontWeight: 700 }}>·</span>;

const COMPONENT_OPTIONS = (['item', 'tax', 'tipsAndGratuity', 'fees'] as RefundComponentFilter[])
    .map((v) => ({ value: v, label: COMPONENT_FILTER_LABEL[v] }));

/** Header order IS column order. Every column sorts. The Service Charge column/grid-track
 * exists only for outlets that capture one (`useServiceChargeEnabled`) — filtered out below,
 * never shown as an empty column. */
const ALL_COLUMNS: Array<{ key: RefundLogSortKey; label: string; right?: boolean }> = [
    { key: 'orderNo', label: 'Order' },
    { key: 'refundedAt', label: 'Refund date' },
    { key: 'type', label: 'Type' },
    { key: 'reason', label: 'Reason' },
    { key: 'staffName', label: 'Staff' },
    { key: 'item', label: 'Item with tax', right: true },
    { key: 'tax', label: 'Tax', right: true },
    { key: 'tipsAndGratuity', label: 'Tips & Grat', right: true },
    { key: 'convenienceFee', label: 'Convenience Fee', right: true },
    { key: 'deliveryFee', label: 'Delivery Fee', right: true },
    { key: 'serviceCharge', label: 'Service Charge', right: true },
    { key: 'cardProcessingFee', label: 'Card Processing Fee', right: true },
    { key: 'total', label: 'Refunded', right: true },
];
// Grid tracks are positional and must match ALL_COLUMNS 1:1 — the 11th token (serviceCharge).
const GRID_ALL = '0.8fr 1fr 0.8fr 1.5fr 1fr 0.8fr 0.8fr 0.9fr 1fr 0.9fr 0.95fr 1.05fr 0.9fr';
const GRID_NO_SC = '0.8fr 1fr 0.8fr 1.5fr 1fr 0.8fr 0.8fr 0.9fr 1fr 0.9fr 1.05fr 0.9fr';

/** Rows-per-page choices for the server-paginated table — matches the Order Log's own
 * convention. The API's own default (10) is the first/initial choice. */
const PAGE_SIZE_CHOICES = [10, 25, 50, 100];

type MenuId = 'type' | 'reason' | 'component' | 'staff' | 'channel' | 'sort';

const MENU_W = 220;
const MENU_MAX_H = 260;
const MENU_MIN_H = 140;   // below this the list is useless — flip above the button instead
const MENU_GAP = 4;       // between the button and the panel
const VIEWPORT_PAD = 12;  // never touch the window edge

/** Exactly one of `top`/`bottom` is set — whichever edge the panel is anchored from. */
interface MenuPos { top?: number; bottom?: number; left: number; maxHeight: number }

/**
 * Place the panel against the VIEWPORT, not the filter row.
 *
 * It used to be `position: absolute` inside the button's own `position: relative`
 * wrapper with a hard-coded `left: 0` — correct for the first filter button, but every
 * button to its right (Staff, Channel, ...) sits further along the row, so the panel
 * opened anchored off-screen on a narrow phone and ran past the right edge (same bug
 * ORD-1.3's Columns menu had before it was fixed this same way — see OrderLogControls.tsx).
 *
 * The upward-opening case anchors from the viewport's BOTTOM edge (CSS `bottom`), not
 * from a computed `top`. `maxHeight` is only a budget — a menu with few options (e.g.
 * Type/Returned's 3 checkboxes) renders shorter than it and the box shrinks to fit. A
 * `top` computed by assuming the full budgeted height left the box's top edge in the
 * right place but its *bottom* edge floating well above the button once the box shrank —
 * the actual bug behind this looking "broken" when opened near the bottom of the screen.
 * Anchoring from `bottom` instead means the box's bottom edge is pinned to the button
 * regardless of how tall its content turns out to be; only its top edge moves.
 */
function placeMenu(btn: HTMLElement): MenuPos {
    const r = btn.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - MENU_GAP - VIEWPORT_PAD;
    const above = r.top - MENU_GAP - VIEWPORT_PAD;
    const openUp = below < MENU_MIN_H && above > below;
    const room = openUp ? above : below;
    const maxHeight = Math.max(MENU_MIN_H, Math.min(MENU_MAX_H, room));
    const left = Math.max(VIEWPORT_PAD, Math.min(r.left, window.innerWidth - MENU_W - VIEWPORT_PAD));
    return openUp
        ? { bottom: window.innerHeight - r.top + MENU_GAP, left, maxHeight }
        : { top: r.bottom + MENU_GAP, left, maxHeight };
}

/**
 * Multi-select dropdown — same look and behaviour as the Order Log's Order Type filter.
 *
 * Checkboxes only touch a local `draft`; `onChange` (and the server refetch it triggers via
 * `filters.types`/`.reasons`/`.staff`/`.channels`) doesn't run until "Apply" is pressed, so
 * ticking three boxes fires one request instead of three.
 */
function FilterMenu<V extends string>({ label, options, selected, open, onOpenChange, onChange, loading, failure, onRetry }: {
    label: string;
    options: Array<{ value: V; label: string }>;
    selected: V[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChange: (next: V[]) => void;
    /** GET /refund-log-filters state — omitted for menus with no API-backed source (Returned). */
    loading?: boolean;
    failure?: boolean;
    onRetry?: () => void;
}) {
    const count = selected.length;
    const [draft, setDraft] = useState<V[]>(selected);
    // Re-seed the draft from the last APPLIED selection every time the menu opens — both for
    // a fresh open, and to discard an unapplied in-progress selection from last time (e.g.
    // the user ticked a box, then clicked away instead of pressing Apply).
    useEffect(() => {
        if (open) setDraft(selected);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    const apply = () => {
        onChange(draft);
        onOpenChange(false);
    };

    const btnRef = useRef<HTMLButtonElement>(null);
    const rafRef = useRef<number | null>(null);
    const [pos, setPos] = useState<MenuPos | null>(null);
    const reposition = useCallback(() => {
        if (btnRef.current) setPos(placeMenu(btnRef.current));
    }, []);
    // Measured before paint so the panel never flashes in the wrong place, same as the
    // Order Log's Columns menu. Capturing the scroll listener so it also fires for this
    // table's own horizontal-scroll wrapper, not just the window.
    //
    // This card sits BELOW three independently-fetched sections (Component Split, Refund
    // Reasons, Refunds by Staff) that can each still be finishing their own late paint
    // (webfont swap, a filter button's own text going from no count to "(2)" and rewrapping,
    // etc.) in the same tick this menu opens — a scroll/resize event never fires for that,
    // so the one synchronous measurement above could be taken a frame before the page
    // settles into its final layout and land the panel against stale coordinates. Two
    // follow-up measurements on the next frames re-confirm (or correct) it once things
    // have genuinely settled, without the flash the first synchronous one is there to avoid.
    useLayoutEffect(() => {
        if (!open) { setPos(null); return; }
        reposition();
        const raf1 = requestAnimationFrame(() => {
            reposition();
            const raf2 = requestAnimationFrame(reposition);
            rafRef.current = raf2;
        });
        rafRef.current = raf1;
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);
        return () => {
            if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
    }, [open, reposition]);

    return (
        <div style={{ position: 'relative' }}>
            <button
                ref={btnRef}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => onOpenChange(!open)}
                style={{
                    height: 32, padding: '0 10px', borderRadius: 6, fontSize: 12, fontFamily: T.font,
                    border: `1px solid ${count ? T.accent : T.cardBorder}`,
                    color: count ? T.accent : T.t2,
                    background: count ? `${T.accent}10` : T.surface,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                }}
            >
                {label}{count > 0 ? ` (${count})` : ''}<span style={{ fontSize: 10 }}>▾</span>
            </button>
            {/* Portalled to <body> so no ancestor's overflow (this table scrolls
                horizontally on mobile) or stacking context can clip it, and positioned
                against the viewport by placeMenu above rather than the button's own
                offset parent. */}
            {open && pos && createPortal(
                <>
                {/* Click-away layer: sits over the whole page, under the open menu. */}
                <div style={{ position: 'fixed', inset: 0, zIndex: 299 }} onClick={() => onOpenChange(false)} />
                <div
                    role="listbox"
                    aria-multiselectable="true"
                    style={{
                        position: 'fixed',
                        ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom }),
                        left: pos.left, zIndex: 300,
                        width: MENU_W, maxHeight: pos.maxHeight, boxSizing: 'border-box',
                        background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: T.shadow,
                        padding: '6px 0', overflowY: 'auto',
                    }}
                >
                    {loading ? (
                        <div style={{ padding: '10px 14px', fontSize: 12, color: T.t3 }}>Loading…</div>
                    ) : failure ? (
                        <div style={{ padding: '10px 14px', fontSize: 12, color: T.t3 }}>
                            Couldn&apos;t load options.{' '}
                            <button
                                type="button"
                                onClick={onRetry}
                                style={{ background: 'none', border: 'none', padding: 0, color: T.accent, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, textDecoration: 'underline' }}
                            >
                                Retry
                            </button>
                        </div>
                    ) : options.length === 0 ? (
                        <div style={{ padding: '10px 14px', fontSize: 12, color: T.t3 }}>No options</div>
                    ) : (
                        <>
                            {options.map((o) => {
                                const on = draft.includes(o.value);
                                return (
                                    <div
                                        key={o.value}
                                        role="option"
                                        aria-selected={on}
                                        onClick={() => setDraft(on ? draft.filter((x) => x !== o.value) : [...draft, o.value])}
                                        style={{
                                            padding: '7px 14px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                                            color: on ? T.accent : T.t1, background: on ? `${T.accent}08` : 'transparent',
                                            display: 'flex', alignItems: 'center', gap: 8,
                                        }}
                                    >
                                        <span style={{
                                            width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                                            border: `1.5px solid ${on ? T.accent : T.t3}`, background: on ? T.accent : 'transparent',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            {on && <span style={{ color: '#fff', fontSize: 9, lineHeight: 1 }}>✓</span>}
                                        </span>
                                        {o.label}
                                    </div>
                                );
                            })}
                            <div style={{ borderTop: `1px solid ${T.divider}`, marginTop: 4, padding: '8px 14px 2px' }}>
                                <button
                                    type="button"
                                    onClick={apply}
                                    style={{
                                        width: '100%', padding: '6px 0', borderRadius: 6, border: 'none',
                                        background: T.accent, color: '#fff', fontSize: 12, fontWeight: 600,
                                        cursor: 'pointer', fontFamily: T.font,
                                    }}
                                >
                                    Apply
                                </button>
                            </div>
                        </>
                    )}
                </div>
                </>,
                document.body,
            )}
        </div>
    );
}

/**
 * Mobile's "Sort" control — same button/portal/list chrome as `FilterMenu` above (so it
 * doesn't look like a different control on the same toolbar), but single-select: picking a
 * row applies it and closes the menu immediately, no "Apply" step, matching how a sort
 * (always exactly one active choice) behaves everywhere else in the app.
 */
function SortMenu({ options, selectedId, selectedLabel, open, onOpenChange, onChange }: {
    options: Array<{ id: string; label: string }>;
    selectedId: string;
    /** Shown on the trigger when `selectedId` doesn't match any option (desktop chose a
     * sort this menu doesn't list) — 'Custom' rather than a wrong label. */
    selectedLabel: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChange: (id: string) => void;
}) {
    const btnRef = useRef<HTMLButtonElement>(null);
    const rafRef = useRef<number | null>(null);
    const [pos, setPos] = useState<MenuPos | null>(null);
    const reposition = useCallback(() => {
        if (btnRef.current) setPos(placeMenu(btnRef.current));
    }, []);
    useLayoutEffect(() => {
        if (!open) { setPos(null); return; }
        reposition();
        const raf1 = requestAnimationFrame(() => {
            reposition();
            const raf2 = requestAnimationFrame(reposition);
            rafRef.current = raf2;
        });
        rafRef.current = raf1;
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);
        return () => {
            if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
    }, [open, reposition]);

    return (
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: T.t3, fontFamily: T.font }}>Sort</span>
            <button
                ref={btnRef}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => onOpenChange(!open)}
                style={{
                    height: 32, padding: '0 10px', borderRadius: 6, fontSize: 12, fontFamily: T.font,
                    border: `1px solid ${T.cardBorder}`, color: T.t1, background: T.surface,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                }}
            >
                {selectedLabel}<span style={{ fontSize: 10 }}>▾</span>
            </button>
            {open && pos && createPortal(
                <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 299 }} onClick={() => onOpenChange(false)} />
                <div
                    role="listbox"
                    style={{
                        position: 'fixed',
                        ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom }),
                        left: pos.left, zIndex: 300,
                        width: MENU_W, maxHeight: pos.maxHeight, boxSizing: 'border-box',
                        background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: T.shadow,
                        padding: '6px 0', overflowY: 'auto',
                    }}
                >
                    {options.map((o) => {
                        const on = o.id === selectedId;
                        return (
                            <div
                                key={o.id}
                                role="option"
                                aria-selected={on}
                                onClick={() => { onChange(o.id); onOpenChange(false); }}
                                style={{
                                    padding: '7px 14px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                                    color: on ? T.accent : T.t1, background: on ? `${T.accent}08` : 'transparent',
                                    display: 'flex', alignItems: 'center', gap: 8,
                                }}
                            >
                                <span style={{
                                    width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                                    border: `1.5px solid ${on ? T.accent : T.t3}`, background: 'transparent',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {on && <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.accent }} />}
                                </span>
                                {o.label}
                            </div>
                        );
                    })}
                </div>
                </>,
                document.body,
            )}
        </div>
    );
}

export function RefundLogTable({ allRecords, startDate, endDate, source, onRowClick, onFiltersChange, exportDateRangeLabel }: {
    /** Whole-period, unfiltered — the same records the cards above read. Drives the "No
     * refunds in this period" empty state and the Staff/Channel filter option universe, so
     * those never shrink to just what the current search happens to match. */
    allRecords: RefundRecord[];
    startDate?: string;
    endDate?: string;
    source: RefundSalesSource;
    onRowClick?: (rec: RefundRecord) => void;
    /** Fired after every filter change, with which filter moved — the page reports it (REF-7). */
    onFiltersChange?: (filters: RefundLogFilters, changed: RefundLogFilterChange) => void;
    /** Human-readable active date range, stamped into the export file header. */
    exportDateRangeLabel?: string;
}) {
    const isDesktop = useIsDesktop();
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const currentLocationId = branch?.id ?? branch?.locationId ?? '';
    // Service Charge column exists only for outlets that capture one — hidden, never an
    // empty column, same gate SnapshotScreen/SalesScreen use for the Service Charge KPI.
    const scEnabled = useServiceChargeEnabled();
    const COLUMNS = useMemo(
        () => (scEnabled ? ALL_COLUMNS : ALL_COLUMNS.filter((c) => c.key !== 'serviceCharge')),
        [scEnabled],
    );
    const GRID = scEnabled ? GRID_ALL : GRID_NO_SC;
    const token = useMemo(() => {
        if (!auth) return null;
        const search = (obj: any, depth = 0): string | null => {
            if (!obj || typeof obj !== 'object' || depth > 3) return null;
            for (const k of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
                if (obj[k] && typeof obj[k] === 'string' && obj[k].length > 10) return obj[k];
            }
            for (const [, v] of Object.entries(obj)) {
                if (typeof v === 'string' && (v as string).length > 50) return v as string;
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    const found = search(v, depth + 1);
                    if (found) return found;
                }
            }
            return null;
        };
        return search(auth);
    }, [auth]);
    const [filters, setFilters] = useState<RefundLogFilters>(EMPTY_REFUND_LOG_FILTERS);
    const [sort, setSort] = useState<RefundLogSort>(DEFAULT_REFUND_LOG_SORT);
    const [openMenu, setOpenMenu] = useState<MenuId | null>(null);
    // 1-indexed, matching the API (page=1 is the first page).
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_CHOICES[0]);

    // `search` is debounced so typing doesn't fire a request per keystroke — same 300ms
    // convention the Order Log's own search uses.
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(filters.search), 300);
        return () => clearTimeout(t);
    }, [filters.search]);

    // Any change that reshapes WHICH rows match (search/sort/server filters/page size, and
    // the client-only Returned filter too, for the same reason) goes back to page 1 — staying
    // on, say, page 3 of a narrower result set would silently show nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setPage(1); }, [
        debouncedSearch, sort.key, sort.dir, pageSize,
        filters.types.join(','), filters.reasons.join(','), filters.staff.join(','), filters.channels.join(','), filters.components.join(','),
    ]);

    const { records: serverRows, totals, totalElements, totalPages, loading, failure, retry } = useRefundLogTableQuery({
        startDate,
        endDate,
        source,
        search: debouncedSearch,
        sortBy: REFUND_LOG_SORT_FIELD[sort.key],
        sortDir: sort.dir,
        type: filters.types,
        reason: filters.reasons,
        staff: filters.staff,
        channel: filters.channels,
        page,
        size: pageSize,
    });

    // REF-2 — Type/Reason/Staff/Channel dropdown contents, from GET /refund-log-filters
    // (not yet deployed — see the hook's own comment). Lazy per menu: nothing fetches until
    // that dropdown is opened.
    const filterOptions = useRefundLogFilterOptions({ startDate, endDate, source });
    const typeOptions = useMemo(
        () => filterOptions.state.type.values.map((v) => ({ value: LABEL_TO_TYPE[v] ?? (v.toLowerCase() as RefundType), label: v })),
        [filterOptions.state.type.values],
    );
    const reasonOptions = useMemo(
        () => filterOptions.state.reason.values.map((v) => ({ value: v, label: v })),
        [filterOptions.state.reason.values],
    );
    const staffOptions = useMemo(
        () => filterOptions.state.staff.values.map((v) => ({ value: v, label: v })),
        [filterOptions.state.staff.values],
    );
    const channelOptions = useMemo(
        () => filterOptions.state.channel.values.map((v) => ({ value: v, label: v })),
        [filterOptions.state.channel.values],
    );

    // Search, sort, Type, Reason, Staff, Channel and paging already happened server-side;
    // only Returned still narrows client-side, on top of whatever the server returned (see
    // filterRefundLog's comment for why it alone has no server equivalent). `totals` (item/
    // tax/tips/fees/grand total) comes straight from the hook — it already covers the WHOLE
    // filtered set regardless of which page is loaded, so it is never recomputed from `rows`.
    const rows = useMemo(() => filterRefundLog(serverRows, filters), [serverRows, filters]);

    // Export mirrors exactly what's on screen, under the active search/type/reason/staff/
    // channel/Returned selection — but the full matching set, not just the on-screen page,
    // so it needs its own fetch rather than reusing the paginated `rows`/`serverRows` above.
    // `opts.locationId` (an "All outlets" export) does the same, for that OTHER outlet.
    const buildLogExportRows = useCallback((recs: RefundRecord[]): ExportCell[][] => recs.map((r) => refundLogExportRow(r, scEnabled)), [scEnabled]);

    // Mirrors the on-screen Total row — straight off the period-wide `RefundLogTotals` the
    // API reports at the response root, the same figures the on-screen footer reads.
    const buildLogTotalsRow = useCallback((count: number, t: RefundLogTotals): ExportCell[] => refundLogExportTotalsRow(count, t, scEnabled), [scEnabled]);

    // The table is server-paginated now, so `rows` only ever holds the on-screen page —
    // export needs the WHOLE filtered set, which means its own fetch (no page/size param
    // sent, so the API's own default — size 500 — returns everything in one response, same
    // as this table did before paging existed). `locationId` lets an "All outlets" export
    // point this at another outlet than the one `useRefundLogTableQuery` is bound to.
    const fetchAllForExport = useCallback(async (locationId: string): Promise<{ recs: RefundRecord[]; totals: RefundLogTotals }> => {
        const apiType = filters.types.map((t) => REFUND_TYPE_LABEL[t]);
        const res: any = await getRefundLogApi({
            locationId,
            startDate, endDate,
            orderSource: source === 'direct' ? 'direct' : source === 'thirdparty' ? 'thirdparty' : undefined,
            search: debouncedSearch || undefined,
            type: apiType,
            reason: filters.reasons,
            staff: filters.staff,
            channel: filters.channels,
            sortBy: REFUND_LOG_SORT_FIELD[sort.key],
            sortDir: sort.dir,
            token,
        }).catch(() => null);
        const content = Array.isArray(res) ? res : (res?.content ?? res?.data ?? []);
        const recs = filterRefundLog(refundLogItemsToRecords(content), filters);
        const totals: RefundLogTotals = Array.isArray(res) || !res
            ? { item: 0, tax: 0, tipsAndGratuity: 0, convenienceFee: 0, deliveryFee: 0, serviceCharge: 0, cardProcessingFee: 0, refunded: 0 }
            : {
                item: Number(res?.totalItemsAmt) || 0,
                tax: Number(res?.totalTaxAmt) || 0,
                tipsAndGratuity: Number(res?.totalTipsAndGratAmt) || 0,
                convenienceFee: Number(res?.totalConvenienceFeeAmt) || 0,
                deliveryFee: Number(res?.totalDeliveryAmt) || 0,
                serviceCharge: Number(res?.totalServiceChargeAmt) || 0,
                cardProcessingFee: Number(res?.totalCardProcessingFeeAmt) || 0,
                refunded: Number(res?.totalRefundedAmt) || 0,
            };
        return { recs, totals };
    }, [filters, debouncedSearch, sort, startDate, endDate, source, token]);

    const buildExport = useCallback(async (_format: ExportFormat, opts?: { locationId?: string }) => {
        // On-screen labels, plus an 'Order date' column right after 'Refund date' (see refundLogExport.ts).
        const headers = refundLogExportHeaders(COLUMNS.map((c) => c.label));
        const { recs, totals: exportTotals } = await fetchAllForExport(opts?.locationId ?? currentLocationId);
        // `totalRow: true` tells the shared exportTable() pipeline the LAST row (the totals
        // row just appended) is a summary — it bolds + shades it in PDF/print, native PDF and
        // Excel, matching every other report's export.
        return { headers, rows: [...buildLogExportRows(recs), buildLogTotalsRow(recs.length, exportTotals)], totalRow: true };
    }, [fetchAllForExport, buildLogExportRows, buildLogTotalsRow, currentLocationId]);

    const update = <K extends keyof RefundLogFilters>(key: K, value: RefundLogFilters[K]) => {
        const next = { ...filters, [key]: value };
        setFilters(next);
        onFiltersChange?.(next, key);
    };
    const clearAll = () => {
        setFilters(EMPTY_REFUND_LOG_FILTERS);
        setOpenMenu(null);
        onFiltersChange?.(EMPTY_REFUND_LOG_FILTERS, 'clear');
    };
    const onSort = (key: RefundLogSortKey) => setSort((prev) => (
        prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: FIRST_CLICK_DIR[key] }
    ));
    const menu = (id: MenuId) => ({
        open: openMenu === id,
        onOpenChange: (open: boolean) => {
            setOpenMenu(open ? id : null);
            // 'component' (Returned) and 'sort' have no API-backed source — every other
            // menu fetches its own options lazily, on first open.
            if (open && id !== 'component' && id !== 'sort') filterOptions.ensureLoaded(id as RefundLogFilterKind);
        },
    });

    const subtitle = isDesktop
        ? 'One row per refund event — a refund posts on the date it occurred, not the order date'
        : 'One row per refund event';

    // "No refunds exist" — the true period-empty case, before any filter is even offered.
    if (allRecords.length === 0) {
        return (
            <RefundCard title="Refund Log" subtitle={subtitle}>
                <SectionEmpty kpiName="refund" size="medium" minHeight={140} message="No refunds in this period" />
            </RefundCard>
        );
    }

    /* ── active-filter chips ── */
    const chips: Array<{ key: string; label: string; remove: () => void }> = [
        ...filters.types.map((v) => ({ key: `t:${v}`, label: `Type: ${REFUND_TYPE_LABEL[v]}`, remove: () => update('types', filters.types.filter((x) => x !== v)) })),
        ...filters.reasons.map((v) => ({ key: `r:${v}`, label: `Reason: ${v}`, remove: () => update('reasons', filters.reasons.filter((x) => x !== v)) })),
        ...filters.components.map((v) => ({ key: `c:${v}`, label: `Returned: ${COMPONENT_FILTER_LABEL[v]}`, remove: () => update('components', filters.components.filter((x) => x !== v)) })),
        ...filters.staff.map((v) => ({ key: `s:${v}`, label: `Staff: ${v}`, remove: () => update('staff', filters.staff.filter((x) => x !== v)) })),
        ...filters.channels.map((v) => ({ key: `ch:${v}`, label: `Channel: ${v}`, remove: () => update('channels', filters.channels.filter((x) => x !== v)) })),
    ];

    const searchField = (
        <div style={{ position: 'relative', flex: isDesktop ? '0 1 260px' : '1 1 100%' }}>
            <input
                value={filters.search}
                onChange={(e) => update('search', e.target.value)}
                placeholder="Search order, reason, staff or guest"
                aria-label="Search refunds"
                style={{
                    width: '100%', height: 32, boxSizing: 'border-box',
                    padding: filters.search ? '0 28px 0 32px' : '0 10px 0 32px',
                    border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1,
                    background: T.surface, outline: 'none', fontFamily: T.font,
                }}
            />
            <span className="material-symbols-rounded" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: T.t3, pointerEvents: 'none' }}>search</span>
            {filters.search && (
                <button
                    type="button"
                    onClick={() => update('search', '')}
                    aria-label="Clear search"
                    style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: T.t3 }}
                >
                    <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
                </button>
            )}
        </div>
    );

    // Mobile has no headers to click, so the useful sorts live in one menu. A sort chosen on
    // desktop that the menu does not list shows as "Custom" rather than as a wrong label.
    const mobileSort = MOBILE_SORTS.find((s) => s.sort.key === sort.key && s.sort.dir === sort.dir);
    const mobileSortId = mobileSort?.id ?? 'custom';
    const sortSelect = (
        <SortMenu
            options={MOBILE_SORTS.map((s) => ({ id: s.id, label: s.label }))}
            selectedId={mobileSortId}
            selectedLabel={mobileSort?.label ?? 'Custom'}
            onChange={(id) => {
                const choice = MOBILE_SORTS.find((s) => s.id === id);
                if (choice) setSort(choice.sort);
            }}
            {...menu('sort')}
        />
    );

    const toolbar = (
        <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                {searchField}
                <FilterMenu
                    label="Type" options={typeOptions} selected={filters.types} onChange={(v) => update('types', v)}
                    loading={filterOptions.state.type.loading} failure={filterOptions.state.type.failure} onRetry={() => filterOptions.retry('type')}
                    {...menu('type')}
                />
                <FilterMenu
                    label="Reason" options={reasonOptions} selected={filters.reasons} onChange={(v) => update('reasons', v)}
                    loading={filterOptions.state.reason.loading} failure={filterOptions.state.reason.failure} onRetry={() => filterOptions.retry('reason')}
                    {...menu('reason')}
                />
                <FilterMenu label="Returned" options={COMPONENT_OPTIONS} selected={filters.components} onChange={(v) => update('components', v)} {...menu('component')} />
                <FilterMenu
                    label="Staff" options={staffOptions} selected={filters.staff} onChange={(v) => update('staff', v)}
                    loading={filterOptions.state.staff.loading} failure={filterOptions.state.staff.failure} onRetry={() => filterOptions.retry('staff')}
                    {...menu('staff')}
                />
                <FilterMenu
                    label="Channel" options={channelOptions} selected={filters.channels} onChange={(v) => update('channels', v)}
                    loading={filterOptions.state.channel.loading} failure={filterOptions.state.channel.failure} onRetry={() => filterOptions.retry('channel')}
                    {...menu('channel')}
                />
                {!isDesktop && sortSelect}
            </div>

            {chips.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                    {chips.map((c) => (
                        <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 10px', background: `${T.accent}12`, borderRadius: 12, fontSize: 11, color: T.accent, fontWeight: 500 }}>
                            {c.label}
                            <span role="button" aria-label={`Remove ${c.label}`} onClick={c.remove} style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1, opacity: 0.7 }}>×</span>
                        </span>
                    ))}
                    <button type="button" onClick={clearAll} style={{ background: 'none', border: 'none', padding: '2px 4px', fontSize: 11, fontWeight: 600, color: T.t2, cursor: 'pointer', textDecoration: 'underline', fontFamily: T.font }}>
                        Clear all
                    </button>
                </div>
            )}

            {/* The filtered set's own totals, split the way the whole page is: what reduced Net
                Sales (items) versus what was cash reconciliation (everything else). `totals`
                and `totalElements` already cover the WHOLE filtered set, not just this page —
                `rows.length` only decides where THIS page's range ends. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 11.5, color: T.t3, lineHeight: 1.5 }}>
                    {totalElements > 0 && (
                        <>Showing <strong style={{ color: T.t1 }}>{(page - 1) * pageSize + 1}</strong>–<strong style={{ color: T.t1 }}>{(page - 1) * pageSize + rows.length}</strong> of {totalElements} refund{totalElements === 1 ? '' : 's'}</>
                    )}
                    {totalElements > 0 && (
                        <>
                            {' · '}<strong style={{ color: T.red }}>{fmtCurrency(totals.refunded)}</strong> refunded
                        </>
                    )}
                </div>
                {totalElements > PAGE_SIZE_CHOICES[0] && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: T.t3 }}>
                        Rows per page:
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            style={{ height: 26, padding: '0 6px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, cursor: 'pointer', fontFamily: T.font }}
                        >
                            {PAGE_SIZE_CHOICES.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                )}
            </div>
        </>
    );

    const noMatches = (
        <div style={{ textAlign: 'center', paddingBottom: 12 }}>
            <SectionEmpty kpiName="refund" size="medium" minHeight={110} message="No refunds match these filters" />
            <button
                type="button"
                onClick={clearAll}
                style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.accent}`, background: 'transparent', color: T.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}
            >
                Clear filters
            </button>
        </div>
    );

    // Reused for both layouts, and by the table's own sort/search/filter refetch — never for
    // the toolbar around it, so the search input stays mounted and keeps focus while typing.
    const shimmerRows = (
        <>
            {Array.from({ length: 4 }).map((_, i) => (
                <Bone key={i} w={null} h={36} r={8} style={{ marginBottom: 8 }} />
            ))}
        </>
    );

    // Server-side pager — desktop gets numbered page buttons (same chrome as the Order
    // Log's own pagination), mobile a compact "page / total" readout between Prev/Next.
    const pagination = totalPages > 1 ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.divider}` }}>
            <span style={{ fontSize: 11, color: T.t3 }}>Page {page} of {totalPages}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: page === 1 ? T.hover : T.surface, color: page === 1 ? T.t3 : T.t1, cursor: page === 1 ? 'default' : 'pointer', fontSize: 12, fontFamily: T.font }}
                >
                    ‹ Prev
                </button>
                {isDesktop ? (
                    Array.from({ length: Math.min(totalPages, 7) }, (_, idx) => {
                        const pg = totalPages <= 7 ? idx + 1 : (page <= 4 ? idx + 1 : (page > totalPages - 4 ? totalPages - 6 + idx : page - 3 + idx));
                        return (
                            <button
                                key={pg}
                                type="button"
                                onClick={() => setPage(pg)}
                                style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${pg === page ? T.accent : T.cardBorder}`, background: pg === page ? T.accent : T.surface, color: pg === page ? '#fff' : T.t2, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: T.font }}
                            >
                                {pg}
                            </button>
                        );
                    })
                ) : (
                    <span style={{ fontSize: 11, color: T.t2, padding: '4px 6px' }}>{page} / {totalPages}</span>
                )}
                <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: page >= totalPages ? T.hover : T.surface, color: page >= totalPages ? T.t3 : T.t1, cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 12, fontFamily: T.font }}
                >
                    Next ›
                </button>
            </div>
        </div>
    ) : null;

    /* ── mobile ── */
    if (!isDesktop) {
        return (
            <RefundCard
                title="Refund Log"
                subtitle={subtitle}
                action={allRecords.length > 0 ? (
                    <ReportSectionExportButton
                        variant="mobile"
                        title="Refund Log"
                        filename="refund-log"
                        dateRangeLabel={exportDateRangeLabel}
                        buildExport={buildExport}
                    />
                ) : undefined}
            >
                {toolbar}
                {failure ? (
                    <SectionError kpiName="refund log" onRetry={retry} size="medium" minHeight={140} />
                ) : loading ? shimmerRows : rows.length === 0 ? noMatches : (
                    <>
                        {rows.map((r) => (
                            <div
                                key={r.id}
                                onClick={onRowClick ? () => onRowClick(r) : undefined}
                                style={{
                                    background: T.bg, border: `1px solid ${T.cardBorder}`, borderRadius: 10,
                                    padding: '10px 12px', marginBottom: 6, cursor: onRowClick ? 'pointer' : 'default',
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>#{r.orderNo}</span>
                                        <RefundTypeBadge type={r.type} />
                                    </span>
                                    <span style={{ fontSize: 12.5, fontWeight: 700, color: T.red, fontVariantNumeric: 'tabular-nums' }}>
                                        {fmtCurrency(refundTotal(r.components))}
                                    </span>
                                </div>
                                <div style={{ fontSize: 11, color: T.accent, fontWeight: 600, marginBottom: 2 }}>{r.staffName}</div>
                                <TruncatedCell text={r.reason} isMobile style={{ fontSize: 10.5, color: T.t2, marginBottom: 3 }} />
                                <div style={{ fontSize: 10, color: T.t3 }}>
                                    {r.refundedAt}
                                    {r.refundedAt !== r.orderDate && <> <Dot /> order {r.orderDate}</>}
                                    {' '}<Dot />{' '}{r.channel}
                                    {' '}<Dot />{' '}item with tax {fmtCurrency(itemRefundAmount(r.components))}
                                    {' '}<Dot />{' '}tax {fmtCurrency(taxRefundAmount(r.components))}
                                    {' '}<Dot />{' '}tips & grat {fmtCurrency(tipsAndGratuityRefundAmount(r.components))}
                                </div>
                            </div>
                        ))}
                        {pagination}
                    </>
                )}
            </RefundCard>
        );
    }

    /* ── desktop ── */
    const head: React.CSSProperties = {
        fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.4,
    };
    const cell: React.CSSProperties = { fontSize: 12, color: T.t1, fontVariantNumeric: 'tabular-nums' };
    const money = (v: number, strong = false): React.ReactNode => (
        <span style={{ ...cell, textAlign: 'right', color: v > 0 ? (strong ? T.red : T.t1) : T.t3, fontWeight: strong ? 700 : undefined }}>
            {v > 0 ? fmtCurrency(v) : '—'}
        </span>
    );

    return (
        <RefundCard
            title="Refund Log"
            subtitle={subtitle}
            action={allRecords.length > 0 ? (
                <ReportSectionExportButton
                    variant="desktop"
                    title="Refund Log"
                    filename="refund-log"
                    dateRangeLabel={exportDateRangeLabel}
                    buildExport={buildExport}
                />
            ) : undefined}
        >
            {toolbar}
            {failure ? (
                <SectionError kpiName="refund log" onRetry={retry} size="medium" minHeight={140} />
            ) : loading ? shimmerRows : rows.length === 0 ? noMatches : (
                <div style={{ overflowX: 'auto' }}>
                    <div role="table" style={{ minWidth: 900 }}>
                        <div role="row" style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, padding: '0 0 8px', borderBottom: `1px solid ${T.divider}` }}>
                            {COLUMNS.map((col) => {
                                const active = sort.key === col.key;
                                return (
                                    <span
                                        key={col.key}
                                        role="columnheader"
                                        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                        style={{ ...head, textAlign: col.right ? 'right' : 'left' }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => onSort(col.key)}
                                            title={`Sort by ${col.label}`}
                                            style={{
                                                all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3,
                                                color: active ? T.t1 : T.t3,
                                            }}
                                        >
                                            {col.label}
                                            <span aria-hidden style={{ fontSize: 9, opacity: active ? 1 : 0.35 }}>
                                                {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                                            </span>
                                        </button>
                                    </span>
                                );
                            })}
                        </div>

                        {rows.map((r) => {
                            const c = r.components;
                            return (
                                <div
                                    key={r.id}
                                    role="row"
                                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                                    style={{
                                        display: 'grid', gridTemplateColumns: GRID, gap: 12, alignItems: 'center',
                                        padding: '10px 0', borderBottom: `1px solid ${T.divider}`,
                                        cursor: onRowClick ? 'pointer' : 'default',
                                    }}
                                >
                                    <span style={{ ...cell, fontWeight: 600 }}>#{r.orderNo}</span>
                                    <span style={{ ...cell, color: T.t2 }}>
                                        {r.refundedAt}
                                        {r.refundedAt !== r.orderDate && (
                                            <span style={{ fontSize: 10, color: T.t3, display: 'block' }}>order {r.orderDate}</span>
                                        )}
                                    </span>
                                    <span><RefundTypeBadge type={r.type} /></span>
                                    {/* A long unbroken reason (no spaces to wrap at) otherwise forces this grid
                                        track past its fr share — pushing every column to its right (Items, Tax, …)
                                        out of alignment with its header. TruncatedCell clips it and shows the full
                                        text on hover/tap instead. */}
                                    <TruncatedCell text={r.reason} style={{ ...cell, color: T.t2, fontSize: 11.5 }} />
                                    <span style={{ ...cell, color: T.t2, fontSize: 11.5 }}>{r.staffName}</span>
                                    {money(c.item)}
                                    {money(c.tax)}
                                    {money(c.tip + c.gratuity)}
                                    {money(c.convenienceFee)}
                                    {money(c.deliveryFee)}
                                    {scEnabled && money(c.serviceCharge)}
                                    {money(c.cardProcessingFee)}
                                    {money(refundTotal(c), true)}
                                </div>
                            );
                        })}

                        {/* Totals for the FILTERED set — it moves with the filters, never the period, and
                            (unlike the rows above it) always covers every page, not just this one. */}
                        <div role="row" style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, alignItems: 'center', padding: '10px 0 2px', borderTop: `2px solid ${T.divider}`, marginTop: -1 }}>
                            <span style={{ gridColumn: '1 / span 5', fontSize: 12, fontWeight: 700, color: T.t1 }}>
                                Total ({totalElements} refund{totalElements === 1 ? '' : 's'})
                            </span>
                            {money(totals.item)}
                            {money(totals.tax)}
                            {money(totals.tipsAndGratuity)}
                            {money(totals.convenienceFee)}
                            {money(totals.deliveryFee)}
                            {scEnabled && money(totals.serviceCharge)}
                            {money(totals.cardProcessingFee)}
                            {money(totals.refunded, true)}
                        </div>
                    </div>
                    {pagination}
                </div>
            )}
        </RefundCard>
    );
}

export default RefundLogTable;
